import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SupportApi } from '../../../../services/api/support/support.api';

function getSupportContext() {
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const timestamp = new Date().toISOString();
  const screenSize =
    typeof window !== 'undefined'
      ? `${window.screen?.width ?? ''}x${window.screen?.height ?? ''}`
      : '';
  const language =
    typeof navigator !== 'undefined' ? navigator.language : '';
  return [
    pageUrl ? `Pagina: ${pageUrl}` : '',
    userAgent ? `Browser: ${userAgent}` : '',
    `Data/Ora: ${timestamp}`,
    screenSize ? `Risoluzione: ${screenSize}` : '',
    language ? `Lingua: ${language}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * @param {{ open: boolean, onClose: function, disablePortal?: boolean }} props
 */
const SupportPopup = ({ open, onClose, disablePortal = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: 'help',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const context = getSupportContext();
      const messageWithContext = formData.message.trim() + '\n\n---\nContesto:\n' + context;
      const payload = {
        ...formData,
        message: messageWithContext,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        timestamp: new Date().toISOString(),
      };
      await SupportApi.sendRequest(payload);
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setFormData({
          type: 'help',
          subject: '',
          message: '',
        });
      }, 2000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error sending support request:', error);
      }
      // Error is already shown via toast system from API interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setSubmitted(false);
      setFormData({
        type: 'help',
        subject: '',
        message: '',
      });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          disablePortal={disablePortal}
          PaperProps={{
            component: motion.div,
            initial: { opacity: 0, scale: 0.8, y: 50 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.8, y: 50 },
            transition: { duration: 0.3, ease: 'easeOut' },
          }}
        >
          <DialogContent sx={{ p: 3, position: 'relative' }}>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
              disabled={loading}
            >
              <Close />
            </IconButton>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    {t('support.request.sent')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('support.response.time')}
                  </Typography>
                </Box>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Typography variant="h6" gutterBottom>
                  {t('support.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('support.description')}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('support.type.label')}</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      label={t('support.type.label')}
                      required
                    >
                      <MenuItem value="help">{t('support.type.help')}</MenuItem>
                      <MenuItem value="info">{t('support.type.info')}</MenuItem>
                      <MenuItem value="other">{t('support.type.other')}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    name="subject"
                    label={t('support.subject')}
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    name="message"
                    label={t('support.message')}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={4}
                    sx={{ mb: 3 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={handleClose} disabled={loading}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || !formData.subject || !formData.message}
                    >
                      {loading ? <CircularProgress size={24} /> : t('support.send')}
                    </Button>
                  </Box>
                </form>
              </motion.div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

SupportPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disablePortal: PropTypes.bool,
};

export default SupportPopup;
